import { useState } from 'react';
import { Building2, Users, TrendingUp, Award, Send, MapPin, Phone, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Layout from '@/components/layout/Layout';
import SEOHead from '@/components/seo/SEOHead';
import PageBreadcrumb from '@/components/PageBreadcrumb';
import { toast } from 'sonner';
import { franchiseFormSchema, sanitizeInput } from '@/lib/validation';

interface FormErrors {
  name?: string;
  email?: string;
  phone?: string;
  city?: string;
  investment?: string;
  message?: string;
}

const Franchise = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    city: '',
    investment: '',
    message: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});

  const benefits = [
    {
      icon: Building2,
      title: 'Established Brand',
      description: 'Join a recognized name in Apple device repairs with proven business model',
    },
    {
      icon: Users,
      title: 'Training & Support',
      description: 'Comprehensive training program and ongoing technical support',
    },
    {
      icon: TrendingUp,
      title: 'High ROI',
      description: 'Growing demand for Apple repairs ensures strong return on investment',
    },
    {
      icon: Award,
      title: 'Quality Standards',
      description: 'Access to genuine parts and standardized repair processes',
    },
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    
    // Validate form data with Zod schema
    const result = franchiseFormSchema.safeParse(formData);
    
    if (!result.success) {
      const fieldErrors: FormErrors = {};
      result.error.errors.forEach((err) => {
        const field = err.path[0] as keyof FormErrors;
        if (field && !fieldErrors[field]) {
          fieldErrors[field] = err.message;
        }
      });
      setErrors(fieldErrors);
      toast.error('Please fix the form errors before submitting.');
      return;
    }

    const validData = result.data;
    
    // Sanitize all text inputs
    const sanitizedName = sanitizeInput(validData.name);
    const sanitizedEmail = sanitizeInput(validData.email);
    const sanitizedPhone = sanitizeInput(validData.phone);
    const sanitizedCity = sanitizeInput(validData.city);
    const sanitizedMessage = validData.message ? sanitizeInput(validData.message) : '';
    
    // Create WhatsApp message with sanitized and properly encoded data
    const message = `*New Franchise Inquiry*

*Name:* ${sanitizedName}
*Email:* ${sanitizedEmail}
*Phone:* ${sanitizedPhone}
*City:* ${sanitizedCity}
*Investment Range:* ${validData.investment || 'Not specified'}
*Message:* ${sanitizedMessage || 'No message provided'}

_Sent from iFix Apple Care website_`;
    
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/919798653591?text=${encodedMessage}`, '_blank');
    toast.success('Inquiry sent! Our team will contact you shortly.');
    setFormData({ name: '', email: '', phone: '', city: '', investment: '', message: '' });
    setErrors({});
  };

  return (
    <Layout>
      <SEOHead
        title="Apple Repair Franchise Bihar | Partner with iFix Apple Care"
        description="Start your Apple repair business in Bihar. Join iFix Apple Care franchise network. Full training, genuine parts supply & ongoing support. Apply today!"
        url="https://ifixappleservicecenter.in/franchise"
      />
      {/* Hero Section */}
      <section className="section-padding bg-secondary pt-28">
        <div className="container-apple">
          <PageBreadcrumb 
            items={[{ name: 'Franchise', path: '/franchise' }]} 
            className="mb-6"
          />
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-foreground leading-tight mb-6 opacity-0 animate-fade-in">
              Partner with
              <span className="block text-primary">iFix Apple Care</span>
            </h1>
            
            <p className="text-lg md:text-xl text-muted-foreground mb-8 leading-relaxed opacity-0 animate-fade-in animation-delay-100">
              Join Bihar's most trusted Apple service network. Become a franchise partner and tap into the 
              growing demand for professional Apple device repairs.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section-padding">
        <div className="container-apple">
          <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-12 text-center">
            Why Partner With Us?
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div
                key={benefit.title}
                className="text-center p-6 opacity-0 animate-fade-in"
                style={{ animationDelay: `${index * 100}ms`, animationFillMode: 'forwards' }}
              >
                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="w-8 h-8 text-primary" />
                </div>
                <h3 className="font-semibold text-foreground text-lg mb-2">{benefit.title}</h3>
                <p className="text-muted-foreground text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Inquiry Form Section */}
      <section className="section-padding bg-secondary">
        <div className="container-apple">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-4">
                Start Your Journey
              </h2>
              <p className="text-muted-foreground mb-8">
                Fill out the form below to express your interest in becoming an iFix Apple Care franchise partner. 
                Our team will get in touch with you to discuss the opportunity.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-background flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground mb-1">Head Office</h4>
                    <p className="text-muted-foreground text-sm">
                      Shop No. 6, Jassinandan Plaza,<br />
                      New Dak Bunglow Rd, Patna, Bihar 800001
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-background flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground mb-1">Phone</h4>
                    <a href="tel:9798653591" className="text-primary hover:underline">
                      +91 97986 53591
                    </a>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-background flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground mb-1">Email</h4>
                    <a href="mailto:franchise@ifixappleservicecenter.in" className="text-primary hover:underline">
                      franchise@ifixappleservicecenter.in
                    </a>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-background rounded-3xl p-8 shadow-apple-md">
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      required
                      maxLength={100}
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className={`w-full h-12 px-4 bg-secondary rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 ${errors.name ? 'ring-2 ring-destructive' : 'focus:ring-primary'}`}
                      placeholder="Your name"
                    />
                    {errors.name && <p className="text-destructive text-sm mt-1">{errors.name}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      required
                      maxLength={10}
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                      className={`w-full h-12 px-4 bg-secondary rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 ${errors.phone ? 'ring-2 ring-destructive' : 'focus:ring-primary'}`}
                      placeholder="10-digit number"
                    />
                    {errors.phone && <p className="text-destructive text-sm mt-1">{errors.phone}</p>}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    required
                    maxLength={255}
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className={`w-full h-12 px-4 bg-secondary rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 ${errors.email ? 'ring-2 ring-destructive' : 'focus:ring-primary'}`}
                    placeholder="Your email"
                  />
                  {errors.email && <p className="text-destructive text-sm mt-1">{errors.email}</p>}
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      City / Location *
                    </label>
                    <input
                      type="text"
                      required
                      maxLength={100}
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      className={`w-full h-12 px-4 bg-secondary rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 ${errors.city ? 'ring-2 ring-destructive' : 'focus:ring-primary'}`}
                      placeholder="Preferred city"
                    />
                    {errors.city && <p className="text-destructive text-sm mt-1">{errors.city}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Investment Range
                    </label>
                    <select
                      value={formData.investment}
                      onChange={(e) => setFormData({ ...formData, investment: e.target.value })}
                      className="w-full h-12 px-4 bg-secondary rounded-xl text-foreground focus:outline-none focus:ring-2 focus:ring-primary appearance-none cursor-pointer"
                    >
                      <option value="">Select range</option>
                      <option value="5-10 Lakhs">₹5-10 Lakhs</option>
                      <option value="10-20 Lakhs">₹10-20 Lakhs</option>
                      <option value="20-50 Lakhs">₹20-50 Lakhs</option>
                      <option value="50+ Lakhs">₹50+ Lakhs</option>
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Message
                  </label>
                  <textarea
                    maxLength={1000}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className={`w-full min-h-[120px] px-4 py-3 bg-secondary rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 resize-none ${errors.message ? 'ring-2 ring-destructive' : 'focus:ring-primary'}`}
                    placeholder="Tell us about yourself and why you're interested"
                  />
                  <div className="flex justify-between mt-1">
                    {errors.message && <p className="text-destructive text-sm">{errors.message}</p>}
                    <p className="text-muted-foreground text-xs ml-auto">{formData.message.length}/1000</p>
                  </div>
                </div>
                
                <Button type="submit" variant="apple-blue" className="w-full" size="lg">
                  <Send className="w-5 h-5" />
                  Submit Inquiry
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Franchise;
